import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { useCreateSite, useSites } from "@/clients/deflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Spinner from "@/components/plugin-shared/spinner";
import type { SiteOrigin } from "deflow-models";
import { formatSiteDomainSummary } from "@/lib/site-domains";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const navigate = useNavigate();
  const { data: sites, error, isLoading } = useSites();
  const { trigger: createSite, isMutating: isCreating } = useCreateSite();

  const [siteName, setSiteName] = useState("");
  const [origin, setOrigin] = useState<SiteOrigin>("static");
  const [formError, setFormError] = useState<string | null>(null);

  const onCreateSite = async () => {
    setFormError(null);
    const name = siteName.trim();
    if (!name) {
      setFormError("Enter a site name.");
      return;
    }

    const [site, err] = await createSite({ siteName: name, origin });
    if (err || !site) {
      setFormError(err?.message ?? "Could not create site.");
      return;
    }

    setSiteName("");
    navigate({ to: "/sites/$siteId", params: { siteId: site.id } });
  };

  return (
    <main className="flex-1 px-8 py-8 max-w-3xl">
      <h1 className="text-2xl font-semibold tracking-tight mb-2">Sites</h1>
      <p className="text-muted-foreground text-sm mb-6">
        Your projects synced from Deflow&apos;s servers.
      </p>

      <div className="border rounded-md p-4 mb-8 space-y-4">
        <h2 className="text-sm font-medium">New site</h2>
        <div className="flex flex-col space-y-1.5">
          <Label htmlFor="siteName">Site name</Label>
          <Input
            id="siteName"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="My site"
          />
        </div>
        <div className="flex flex-col space-y-1.5">
          <Label>Origin</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={origin === "static" ? "default" : "outline"}
              onClick={() => setOrigin("static")}
            >
              Static
            </Button>
            <Button
              type="button"
              size="sm"
              variant={origin === "figma" ? "default" : "outline"}
              onClick={() => setOrigin("figma")}
            >
              Figma
            </Button>
          </div>
        </div>
        {formError ? (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        ) : null}
        <Button
          disabled={isCreating || siteName.trim().length === 0}
          onClick={() => void onCreateSite()}
        >
          {isCreating ? <Spinner /> : "Create site"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          Unable to load sites right now.
        </p>
      ) : sites && sites.length > 0 ? (
        <ul className="space-y-3">
          {sites.map((site) => (
            <li
              key={site.id}
              className="border rounded-md px-4 py-3 flex flex-col gap-1"
            >
              <Link
                to="/sites/$siteId"
                params={{ siteId: site.id }}
                className="font-medium hover:underline"
              >
                {site.siteName}
              </Link>
              <span className="text-xs text-muted-foreground">
                {formatSiteDomainSummary(site)}
                {site.origin ? ` · ${site.origin}` : ""}
                {site.designLinkStatus ? ` · ${site.designLinkStatus}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No sites yet. Create one above.
        </p>
      )}
    </main>
  );
}
